"use client"

import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { Confetti } from "@/app/components/confetti"
import { Copy } from "@/app/components/copy/copy"
import { ScannerLink } from "@/app/components/scanner-link"
import { getCollectionHref } from "@/app/navigation/collection"
import { useConnectStatus } from "@/app/nft/order/widget/actions/use-connect-status"
import { useEvmWhitelisting } from "@/app/sdk/action-molecules/evm/evm-whitelisting-molecule"
import { CollectionScope } from "@/app/sdk/anaconda-sdk-scope"
import { useWalletConnect } from "@/app/wallet-connect/hooks"
import { getRawAddress } from "@anaconda/core/blockchain/address"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  cn,
  useIsMobile,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { ScopeProvider } from "bunshi/react"
import { useMolecule } from "bunshi/react"
import Link from "next/link"
import { useCallback, useMemo, useState } from "react"

export interface CollectionListingDialogProps {
  collectionId: CollectionId
  collectionName: string
  blockchain: Blockchain
  image?: string
  isOpen: boolean
  onClose: () => void
}

interface ContentProps {
  collectionId: CollectionId
  collectionName: string
  image?: string
  blockchain: Blockchain
}

const CollectionListingContent = reatomComponent(
  ({ collectionId, collectionName, image, blockchain }: ContentProps) => {
    const onSuccess = useCallback(() => {
      setIsSuccessListing(true)
    }, [])

    const { mutate, isPending } = useEvmWhitelisting({ onSuccess })
    const { status } = useWalletConnect()

    const blockchainsMolecule = useMolecule(BlockchainsMoleculeReatom)
    const scanerLink =
      blockchainsMolecule.reatomScannerLinkByBlockchain()(blockchain)
    const rawAddress = useMemo(() => {
      return getRawAddress(collectionId)
    }, [collectionId])

    const formattedAddress = useMemo(() => {
      return `${rawAddress.slice(0, 6)}...${rawAddress.slice(-4)}`
    }, [rawAddress])

    const [isSuccessListing, setIsSuccessListing] = useState(false)

    const connectAndList = useConnectStatus(() => {
      mutate({ onSuccess })
    })

    const handleListCollection = () => {
      if (status === "connected") {
        mutate({ onSuccess })
      } else {
        connectAndList()
      }
    }

    return (
      <>
        <div className="mb-4 space-y-4">
          {image && (
            <div className="flex justify-center">
              <img
                src={image}
                alt={collectionName}
                className="h-[64px] w-[64px] rounded-lg object-cover"
              />
            </div>
          )}

          <h2
            className={cn(
              "text-center font-headline text-l text-white",
              isSuccessListing
                ? "font-medium text-[28px] leading-[36px]"
                : "font-normal leading-[24px]",
            )}
          >
            {isSuccessListing ? (
              <Trans>{collectionName} successfully listed!</Trans>
            ) : (
              collectionName
            )}
          </h2>
        </div>

        <div className="space-y-0 rounded-lg border py-4">
          <div className="flex items-center justify-between px-4 py-2">
            <span className="text-gray-400 text-sm">Contract Address</span>
            <div className="flex items-center gap-2">
              <Copy copyValue={rawAddress} withIcon={true} asChild>
                {<span>{formattedAddress}</span>}
              </Copy>
              <ScannerLink
                url={`${scanerLink}/address/${rawAddress}`}
                className="text-foreground/60"
              />
            </div>
          </div>

          <div className="flex items-center justify-between px-4 py-2">
            <span className="text-gray-400 text-sm">Chain</span>
            <span className="text-sm text-white">{blockchain}</span>
          </div>

          <div className="flex items-center justify-between px-4 py-2">
            <span className="text-gray-400 text-sm">Token Standard</span>
            <span className="text-sm text-white">ERC-721</span>
          </div>
        </div>

        {!isSuccessListing && (
          <div className="rounded-lg border p-4">
            <p className="text-gray-400 text-sm">
              <Trans>
                $RARI token on Base Chain will be used to pay for collection
                listing
              </Trans>
            </p>
          </div>
        )}

        {!isSuccessListing && (
          <div className="space-y-4">
            <Button
              onClick={handleListCollection}
              disabled={isPending}
              className="h-12 w-full rounded-lg font-semibold text-base text-black"
            >
              {isPending ? (
                <Trans>Whitelisting...</Trans>
              ) : (
                <Trans>List for 1,000 RARI</Trans>
              )}
            </Button>

            <Link href="/base/swap" className="flex">
              <Button
                variant="outline"
                className="h-12 w-full rounded-lg border bg-transparent font-semibold text-base text-white hover:bg-gray-800"
              >
                <Trans>Buy RARI</Trans>
              </Button>
            </Link>
          </div>
        )}

        {isSuccessListing && (
          <Link href={getCollectionHref(collectionId)}>
            <Button className="h-12 w-full rounded-lg font-semibold text-base text-black">
              <Trans>View Collection</Trans>
            </Button>
          </Link>
        )}

        {isSuccessListing && <Confetti />}
      </>
    )
  },
  "CollectionListingContent",
)

function CollectionListingDialogContent(props: ContentProps) {
  return (
    <>
      <DialogHeader className="mb-8">
        <DialogTitle className="sr-only">{props.collectionName}</DialogTitle>
      </DialogHeader>
      <CollectionListingContent {...props} />
    </>
  )
}

function CollectionListingSheetContent(props: ContentProps) {
  return (
    <div className="flex flex-col gap-4">
      <SheetHeader className="mb-8">
        <SheetTitle className="sr-only">{props.collectionName}</SheetTitle>
      </SheetHeader>
      <CollectionListingContent {...props} />
    </div>
  )
}

export function CollectionListingDialog({
  collectionId,
  collectionName,
  blockchain,
  image,
  isOpen,
  onClose,
}: CollectionListingDialogProps) {
  const isMobile = useIsMobile()

  const collection = useMemo(
    () => ({
      id: collectionId,
      blockchain,
      name: collectionName,
      image,
    }),
    [collectionId, blockchain, collectionName, image],
  )

  const contentProps: ContentProps = {
    collectionId,
    collectionName,
    blockchain,
    image,
  }

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent
          className="w-full gap-4 rounded-t-xl bg-background text-white"
          side="bottom"
        >
          <ScopeProvider scope={CollectionScope} value={collection}>
            <CollectionListingSheetContent {...contentProps} />
          </ScopeProvider>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md border bg-background text-white">
        <ScopeProvider scope={CollectionScope} value={collection}>
          <CollectionListingDialogContent {...contentProps} />
        </ScopeProvider>
      </DialogContent>
    </Dialog>
  )
}
